<template>
  <div
    class="dashboard-grid"
    :class="{ 'edit-mode': editMode, 'drag-over': isDragOver, 'single-column': columns === 1 }"
  >
    <div
      v-if="isDragOver && editMode"
      class="drag-over-message"
    >
      <p>Add to section</p>
    </div>
    <div
      v-else-if="layout.length === 0"
      class="no-layout"
    >
      No layout items ({{ props.modelValue.length }} widgets)
    </div>
    <div v-else>
      <grid-layout
        :key="`grid-${columns}-${rowHeight}`"
        :layout="combinedLayout"
        :col-num="columns"
        :row-height="rowHeight"
        :is-draggable="editMode"
        :is-resizable="editMode"
        :vertical-compact="true"
        :use-css-transforms="true"
        :margin="responsiveMargin"
        :auto-size="true"
        @layout-updated="onLayoutUpdated"
        @layout-changed="onLayoutUpdated"
      >
        <grid-item
          v-for="item in combinedLayout"
          :key="`${item.i}-${columns}`"
          :i="item.i"
          :x="item.x"
          :y="item.y"
          :w="1"
          :h="1"
          :min-w="1"
          :min-h="1"
          :max-w="1"
          :max-h="1"
          :static="false"
          :is-draggable="editMode && !item.isEmpty"
          :is-resizable="editMode && !item.isEmpty"
          :class="['widget-grid-item', {
            'empty-slot': item.isEmpty,
            'hidden-widget': isDragOver && !item.isEmpty
          }]"
          @move="onItemMove"
        >
          <div 
            v-if="!item.isEmpty" 
            class="widget-container" 
            :data-widget-id="item.i"
          >
            <component 
              :is="getWidgetComponent(item.i)"
              v-if="getWidgetComponent(item.i)" 
              v-bind="getWidgetProps(item.i) || {}"
              @update:props="(newProps) => handleWidgetPropsUpdate(item.i, newProps)"
              @open-settings="handleOpenSettings"
              @remove-widget="(widgetId) => emit('remove-widget', widgetId)"
            />
            <div
              v-else
              class="widget-error"
            >
              Widget component not found for: {{ item.i }}
            </div>
          </div>
          <div 
            v-else 
            class="empty-slot-placeholder" 
            @click="() => handleEmptySlotClick(item)"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="empty-slot-icon"
            >
              <path
                d="M12 5V19M5 12H19"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <span class="empty-slot-label">Add</span>
          </div>
        </grid-item>
      </grid-layout>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, provide } from 'vue'
import { GridLayout, GridItem } from 'vue-grid-layout-v3'

const props = defineProps({
  modelValue: {
    type: Array,
    required: true
  },
  columns: {
    type: Number,
    default: 3
  },
  gap: {
    type: Number,
    default: 20
  },
  rowHeight: {
    type: Number,
    default: 360
  },
  editMode: {
    type: Boolean,
    default: false
  },
  sectionIndex: {
    type: Number,
    default: 0
  },
  sectionId: {
    type: String,
    default: null
  },
  isDragOver: {
    type: Boolean,
    default: false
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'open-settings', 'remove-widget', 'open-add-widget'])

// Provide section index so child widgets can auto-detect if they're in the top section
provide('sectionIndex', props.sectionIndex)

// Track the widget currently being dragged (set by grid-item @move event)
const draggingItemId = ref(null)
const onItemMove = (i) => {
  draggingItemId.value = i
}

// Track window width for responsive margin
const windowWidth = ref(window.innerWidth)

const handleResize = () => {
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

// Responsive margin: no horizontal spacing on mobile when single column, keep vertical spacing
// If multiple columns on mobile, keep horizontal spacing
const responsiveMargin = computed(() => {
  const isMobile = windowWidth.value <= 768
  if (isMobile && props.columns === 1) {
    // Single column on mobile: no horizontal spacing, keep vertical
    return [0, props.gap]
  }
  // Multiple columns or desktop: keep both horizontal and vertical spacing
  return [props.gap, props.gap]
})

// Convert widgets to vue-grid-layout format
const layout = ref([])
const isUpdatingFromEvent = ref(false)
const isHandlingColumnChange = ref(false)

// Calculate the single add placeholder: always at the next cell after the last widget (bottom-right).
// Placeholder cannot be reordered; widgets are compacted so none end up to the right or below it.
const emptySlots = computed(() => {
  if (!props.editMode || layout.value.length === 0) {
    return []
  }
  const items = layout.value
  // Find the last widget in row-major order (largest y, then largest x in that row)
  let lastY = -1
  let lastX = -1
  items.forEach(item => {
    const y = item.y
    const x = item.x
    if (y > lastY || (y === lastY && x > lastX)) {
      lastY = y
      lastX = x
    }
  })
  // Next cell after last widget (row-major order)
  let nextX = lastX + 1
  let nextY = lastY
  if (nextX >= props.columns) {
    nextX = 0
    nextY += 1
  }
  return [{
    i: `empty-${nextX}-${nextY}`,
    x: nextX,
    y: nextY,
    w: 1,
    h: 1,
    isEmpty: true
  }]
})

// Combine layout with empty slots. When layout already has empty slots (from grid),
// use it as-is to avoid triggering recursive re-emits.
// When not in edit mode, exclude empty slots so the Add placeholder is hidden after save.
const combinedLayout = computed(() => {
  if (!props.editMode) {
    return layout.value.filter(item => !item.isEmpty)
  }
  if (layout.value.some(item => item.isEmpty)) {
    return layout.value
  }
  return [...layout.value, ...emptySlots.value]
})

// Check if a position is occupied by any widget
const isPositionOccupied = (x, y, w, h, layout, excludeId = null) => {
  for (let checkY = y; checkY < y + h; checkY++) {
    for (let checkX = x; checkX < x + w; checkX++) {
      const occupied = layout.some(item => {
        if (item.i === excludeId) return false
        return checkX >= item.x && 
               checkX < item.x + item.w &&
               checkY >= item.y && 
               checkY < item.y + item.h
      })
      if (occupied) return true
    }
  }
  return false
}

// Initialize layout from widgets - position from array order (row-major)
const initializeLayout = () => {
  if (!props.modelValue || props.modelValue.length === 0) {
    layout.value = []
    return
  }

  const newLayout = props.modelValue.map((widget, index) => {
    const x = index % props.columns
    const y = Math.floor(index / props.columns)
    return {
      i: widget.id,
      x,
      y,
      w: 1,
      h: 1
    }
  })

  layout.value = newLayout
}

// When exiting edit mode, remove empty slots from layout so we don't persist them
watch(() => props.editMode, (editMode) => {
  if (!editMode && layout.value.some(item => item.isEmpty)) {
    layout.value = layout.value.filter(item => !item.isEmpty)
  }
})

// Initialize on mount and when widgets change
watch(() => props.modelValue, () => {
  if (isUpdatingFromEvent.value) {
    return // Skip if we're updating from a layout event
  }
  try {
    initializeLayout()
  } catch (error) {
    console.error('Error initializing layout:', error)
  }
}, { immediate: true, deep: true })

// Recalculate layout when columns change
watch(() => props.columns, (newColumns, oldColumns) => {
  if (isUpdatingFromEvent.value || !oldColumns) {
    return // Skip if we're updating from a layout event or initial mount
  }
  try {
    // Set flag to prevent saving layout updates during automatic column changes
    isHandlingColumnChange.value = true
    
    // When columns change, recalculate the entire layout
    // This ensures widgets are repositioned to fit the new column count
    initializeLayout()
    
    // Reset flag after layout has been recalculated
    // Use a timeout to ensure any vue-grid-layout internal updates have completed
    setTimeout(() => {
      isHandlingColumnChange.value = false
    }, 100)
  } catch (error) {
    console.error('Error recalculating layout for column change:', error)
    isHandlingColumnChange.value = false
  }
}, { immediate: false })

const getWidgetComponent = (id) => {
  const widget = props.modelValue.find(w => w.id === id)
  return widget?.component
}

const getWidgetProps = (id) => {
  const widget = props.modelValue.find(w => w.id === id)
  const layoutItem = layout.value.find(item => item.i === id)
  
  // Calculate priority based on section index and y position
  // Higher priority = lower section index and lower y position (top widgets load first)
  // Use a large multiplier for section index to ensure top sections load before lower sections
  // Then add y position within the section
  const sectionPriority = (1000 - props.sectionIndex) * 1000 // Higher section index = lower priority
  const yPosition = layoutItem?.y || 0
  const priority = sectionPriority + (1000 - yPosition) // Higher y = lower priority
  
  // Pass logical spans so widgets show full content (tables, extra columns).
  // Grid layout is 1x1 but each cell is tall (360px ≈ 3 rows). Tell widgets they have room.
  const logicalRowSpan = 3
  const logicalColSpan = 2
  // Derive widget type (base id) from instance id, e.g. "plans-ending-soon-123-456" -> "plans-ending-soon"
  const widgetType = id.includes('-') ? id.replace(/-\d+(-\d+)*$/, '') : id
  return {
    props: widget?.props || {}, // Pass props object so widgets can access it (e.g., props.props.dateRange)
    editMode: props.editMode,
    rowSpan: props.focused ? 1 : logicalRowSpan,
    colSpan: logicalColSpan,
    widgetId: id, // Pass widget ID so widgets can use it for removal
    sectionId: props.sectionId,
    widgetType,
    priority: priority, // Pass calculated priority for data loading
    focused: props.focused
  }
}

// Handle widget prop updates (e.g., date range changes)
const handleWidgetPropsUpdate = (widgetId, newProps) => {
  const updatedWidgets = props.modelValue.map(widget => {
    if (widget.id === widgetId) {
      return {
        ...widget,
        props: {
          ...(widget.props || {}),
          ...newProps
        }
      }
    }
    return widget
  })
  emit('update:modelValue', updatedWidgets)
}

const handleOpenSettings = () => {
  emit('open-settings')
}

const handleEmptySlotClick = (item) => {
  if (props.editMode) {
    emit('open-add-widget', {
      position: {
        x: item.x,
        y: item.y
      }
    })
  }
}

// vue-grid-layout only moves displaced items vertically (up/down), never horizontally.
// When we drag widget A over widget B, vue-grid-layout cascades displacements down through rows.
// Use insert semantics: remove the dragged item from its old position and insert it at the
// target, shifting everything else over. The dragged item ID is tracked via the @move event.
const applyMoveIfNeeded = (newLayout, prevLayout) => {
  if (!prevLayout || prevLayout.length === 0) return newLayout
  if (!draggingItemId.value) return newLayout

  const prevWidgets = prevLayout.filter(item => !item.isEmpty)
  const newWidgets = newLayout.filter(item => !item.isEmpty)
  if (prevWidgets.length !== newWidgets.length) return newLayout

  const draggedInNew = newWidgets.find(w => w.i === draggingItemId.value)
  if (!draggedInNew) return newLayout

  const draggedInPrev = prevWidgets.find(p => p.i === draggingItemId.value)
  if (!draggedInPrev || (draggedInPrev.x === draggedInNew.x && draggedInPrev.y === draggedInNew.y)) return newLayout

  // Previous order by grid position
  const prevOrder = [...prevWidgets].sort((a, b) => a.y - b.y || a.x - b.x)

  const sourceIndex = prevOrder.findIndex(p => p.i === draggingItemId.value)
  const targetIndex = prevOrder.findIndex(p => p.x === draggedInNew.x && p.y === draggedInNew.y)

  if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) return newLayout

  // Remove from old position and insert at new position
  const reordered = [...prevOrder]
  const [moved] = reordered.splice(sourceIndex, 1)
  reordered.splice(targetIndex, 0, moved)

  // Assign row-major positions so enforceValidLayout's sort preserves this order
  const cols = props.columns
  const newLayoutMap = Object.fromEntries(newLayout.map(item => [item.i, item]))
  return [
    ...reordered.map((item, index) => ({
      ...(newLayoutMap[item.i] || item),
      x: index % cols,
      y: Math.floor(index / cols),
      w: 1,
      h: 1
    })),
    ...newLayout.filter(item => item.isEmpty)
  ]
}

// Enforce valid layout: widgets in row-major order (0,0), (1,0), (2,0), (0,1)... add placeholder at end.
// Prevents widgets from being placed under others or after the add placeholder.
const enforceValidLayout = (currentLayout) => {
  const widgets = currentLayout.filter(item => !item.isEmpty).sort((a, b) => a.y - b.y || a.x - b.x)
  const cols = props.columns
  const result = widgets.map((item, index) => {
    const x = index % cols
    const y = Math.floor(index / cols)
    return { ...item, x, y, w: 1, h: 1 }
  })
  const lastWidget = result[result.length - 1]
  let addX = 0
  let addY = 0
  if (lastWidget) {
    addX = lastWidget.x + 1
    addY = lastWidget.y
    if (addX >= cols) {
      addX = 0
      addY = lastWidget.y + 1
    }
  }
  result.push({
    i: `empty-${addX}-${addY}`,
    x: addX,
    y: addY,
    w: 1,
    h: 1,
    isEmpty: true
  })
  return result
}

// Move widgets up if space becomes available above them
const compactUp = (currentLayout) => {
  // Create a copy to avoid mutating the original
  const sortedLayout = [...currentLayout].sort((a, b) => a.y - b.y || a.x - b.x)
  const compactedLayout = []
  let hasChanges = false
  
  sortedLayout.forEach(item => {
    let newY = item.y
    
    // Try moving this widget up row by row
    for (let testY = item.y - 1; testY >= 0; testY--) {
      if (!isPositionOccupied(item.x, testY, item.w, item.h, compactedLayout)) {
        newY = testY
        hasChanges = true
      } else {
        break // Can't move further up
      }
    }
    
    compactedLayout.push({
      ...item,
      y: newY
    })
  })
  
  // Only return compacted layout if there were actual changes
  // This prevents unnecessary updates
  return hasChanges ? compactedLayout : currentLayout
}

const onLayoutUpdated = (newLayout) => {
  try {
    // Don't save layout updates during automatic column changes
    if (isHandlingColumnChange.value) {
      const widgetLayout = newLayout.filter(item => !item.isEmpty)
      layout.value = widgetLayout
      return
    }

    isUpdatingFromEvent.value = true

    // Move: when dragging widget A over B, remove A from its old slot and insert at B's position.
    const prevLayout = layout.value
    const layoutWithSwap = applyMoveIfNeeded(newLayout, prevLayout)

    // Enforce valid layout: widgets in row-major, add placeholder at end. Prevents placing
    // widgets under others or after the add placeholder. Result is already compact so grid
    // won't re-emit.
    const validLayout = enforceValidLayout(layoutWithSwap)
    layout.value = validLayout

    // Filter to widgets only for the emit to parent
    const widgetLayout = validLayout.filter(item => !item.isEmpty).map(item => ({ ...item, w: 1, h: 1 }))
    const updatedWidgets = widgetLayout.map(item => {
      const originalWidget = props.modelValue.find(w => w.id === item.i)
      if (!originalWidget) return null
      return {
        ...originalWidget,
        colSpan: 1,
        rowSpan: 1
      }
    }).filter(Boolean)

    emit('update:modelValue', updatedWidgets)

    setTimeout(() => {
      isUpdatingFromEvent.value = false
      draggingItemId.value = null
    }, 0)
  } catch (error) {
    console.error('Error updating layout:', error)
    isUpdatingFromEvent.value = false
  }
}
</script>

<style scoped>
.dashboard-grid {
  width: 100%;
  height: auto;
  overflow: visible;
  min-height: 0;
  position: relative;
  /* Ensure grid takes up space and pushes down subsequent sections */
  display: block;
  clear: both;
}

.dashboard-grid.edit-mode {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.dashboard-grid.edit-mode * {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.widget-grid-item {
  /* Disable transitions to prevent animation on reload/expand/collapse */
  transition: none;
}

.widget-container {
  width: 100%;
  height: 100%;
  position: relative;
  min-height: 100px;
  overflow: visible;
  pointer-events: auto;
}

/* Override vue-grid-layout styles for better appearance */
:deep(.vue-grid-layout) {
  background-color: rgba(247, 247, 247, 1);
  border-radius: 16px;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
  border-bottom-left-radius: 16px;
  min-height: 0;
  /* Don't override height - let vue-grid-layout calculate it based on grid items */
  position: relative;
  width: 100%;
  overflow: visible;
  /* Ensure grid layout calculates proper height for stacking */
  display: block;
}

/* Single column (small screens): inset widgets to match standard 8px gap between widgets */
.dashboard-grid.single-column :deep(.vue-grid-layout) {
  padding-left: 8px;
  padding-right: 8px;
  box-sizing: border-box;
}

.dashboard-grid.single-column :deep(.vue-grid-item > *) {
  width: calc(100% - 16px) !important;
  margin-left: 8px;
  margin-right: 8px;
  box-sizing: border-box;
}

:deep(.vue-grid-item) {
  /* Disable transitions on initial render and expand/collapse */
  /* Only allow transitions when dragging/resizing (handled by vue-grid-layout) */
  transition: none !important;
  transition-property: none !important;
  background: transparent;
  border: none;
  box-sizing: border-box;
  overflow: visible;
}

/* Override vue-grid-layout's transition properties */
:deep(.vue-grid-item[data-v-99aff433]) {
  transition: none !important;
  transition-property: none !important;
}

:deep(.vue-grid-item.cssTransforms[data-v-99aff433]) {
  transition-property: none !important;
}

/* Re-enable transitions only when actively dragging or resizing */
:deep(.vue-grid-item.vue-draggable-dragging),
:deep(.vue-grid-item.resizing) {
  transition: none;
}

/* Allow clicks on interactive elements inside widgets even in edit mode */
:deep(.vue-grid-item:not(.vue-grid-placeholder)) button,
:deep(.vue-grid-item:not(.vue-grid-placeholder)) a,
:deep(.vue-grid-item:not(.vue-grid-placeholder)) [role="button"],
:deep(.vue-grid-item:not(.vue-grid-placeholder)) .task-row,
:deep(.vue-grid-item:not(.vue-grid-placeholder)) .booking-item,
:deep(.vue-grid-item:not(.vue-grid-placeholder)) .tour-item,
:deep(.vue-grid-item:not(.vue-grid-placeholder)) .assignment-item,
:deep(.vue-grid-item:not(.vue-grid-placeholder)) .message-item {
  pointer-events: auto;
  cursor: pointer;
}

:deep(.vue-grid-item > *) {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

:deep(.vue-grid-item > *) {
  width: 100%;
  height: 100%;
}

:deep(.vue-grid-item.vue-grid-placeholder) {
  background: #e0e0e0 !important;
  border: none !important;
  border-radius: 12px;
  opacity: 0.6 !important;
  transition: none !important;
  z-index: 1 !important;
  pointer-events: none !important;
}

:deep(.vue-resizable-handle) {
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath d='M2 2 L14 14 M14 10 L14 14 L10 14' stroke='%233b82f6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E") no-repeat;
  background-position: bottom right;
  background-size: 16px 16px;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.2s;
  padding-bottom: 8px !important;
  padding-right: 8px !important;
  padding-top: 0 !important;
  padding-left: 0 !important;
  left: unset !important;
  top: unset !important;
}

:deep(.vue-grid-item > .vue-resizable-handle) {
  padding-bottom: 8px !important;
  padding-right: 8px !important;
  padding-top: 0 !important;
  padding-left: 0 !important;
  left: unset !important;
  top: unset !important;
}

:deep(.vue-grid-item:not(.empty-slot):hover .vue-resizable-handle) {
  opacity: 1;
}

/* Hide resize handle in edit mode (widgets are fixed 1x1) */
.edit-mode :deep(.vue-grid-item .vue-resizable-handle) {
  display: none !important;
}

.empty-slot-placeholder {
  width: 100%;
  height: 100%;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.empty-slot-label {
  font-size: 14px;
  font-weight: 500;
  color: #2383e2;
  margin-top: 2px;
}

.empty-slot-placeholder:hover {
  border-color: #2383e2;
  background-color: rgba(35, 131, 226, 0.05);
}

.empty-slot-icon {
  opacity: 1;
  transition: opacity 0.2s;
  color: #2383e2;
  pointer-events: none;
}

.empty-slot-placeholder:hover .empty-slot-icon {
  opacity: 1;
  color: #2383e2;
}

:deep(.vue-resizable-handle-se) {
  width: 16px;
  height: 16px;
  right: -4px !important;
  bottom: -4px !important;
  cursor: nwse-resize;
  border-radius: 4px 0 12px 0;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath d='M2 2 L14 14 M14 10 L14 14 L10 14' stroke='%233b82f6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E") no-repeat;
  background-position: bottom right;
  background-size: 16px 16px;
  padding-bottom: 8px !important;
  padding-right: 8px !important;
  padding-top: 0 !important;
  padding-left: 0 !important;
}

:deep(.vue-resizable-handle-e) {
  width: 8px;
  right: 0;
  top: 0;
  bottom: 0;
  cursor: ew-resize;
}

:deep(.vue-resizable-handle-s) {
  height: 8px;
  bottom: 0;
  left: 0;
  right: 0;
  cursor: ns-resize;
}

.widget-error {
  padding: 20px;
  background: #fee;
  color: #c33;
  border: 1px solid #fcc;
  border-radius: 8px;
  text-align: center;
}

.no-layout {
  padding: 40px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  background: #f9f9f9;
  border-radius: 8px;
}

.drag-over-message {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 60px 20px;
  text-align: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: rgba(255, 255, 255, 0.95);
}

.drag-over-message p {
  font-size: 16px;
  font-weight: 500;
  color: #2383E2;
  margin: 0;
}

.dashboard-grid.drag-over {
  position: relative;
}

.dashboard-grid.drag-over .widget-grid-item.hidden-widget {
  opacity: 0;
  pointer-events: none;
}

.dashboard-grid.drag-over :deep(.vue-grid-layout) {
  opacity: 0;
  pointer-events: none;
}

@media (max-width: 768px) {
  .dashboard-grid {
    padding: 0;
    margin: 0;
    width: 100%;
    max-width: 100%;
  }
  
  :deep(.vue-grid-layout) {
    margin-left: 0 !important;
    margin-right: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    left: 0 !important;
    right: 0 !important;
  }
  
  :deep(.vue-grid-item) {
    margin-left: 0 !important;
    margin-right: 0 !important;
    left: 0 !important;
  }
  
  :deep(.vue-grid-item[style*="left"]) {
    left: 0 !important;
  }
  
  :deep(.vue-grid-item[style*="margin"]) {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}

</style>
